import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import { askPdfOrXlsx } from '../utils/pdfPrint';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3, Settings, Save } from 'lucide-react';

const GeneralAccountingManagement = memo(() => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, income, expenses
  
  // Overview data
  const [summary, setSummary] = useState(null);
  const [recentIncome, setRecentIncome] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);
  // 🆕 Cashbox evolution chart
  const [chartPeriod, setChartPeriod] = useState('daily'); // daily | weekly | monthly
  const [chartDays, setChartDays] = useState(30);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  
  // Income data
  const [incomeList, setIncomeList] = useState([]);
  const [incomeLoading, setIncomeLoading] = useState(false);
  
  // Expense data  
  const [expensesList, setExpensesList] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  
  // Form states for creating income
  const [incomeAmount, setIncomeAmount] = useState('');
  const [incomeDescription, setIncomeDescription] = useState('');
  const [incomeType, setIncomeType] = useState('other_income');
  const [incomeSource, setIncomeSource] = useState('');
  const [incomeInvoice, setIncomeInvoice] = useState('');
  
  // Form states for creating expenses
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseType, setExpenseType] = useState('office_rent');
  const [expenseVendor, setExpenseVendor] = useState('');
  const [expenseInvoice, setExpenseInvoice] = useState('');
  const [expenseEmployee, setExpenseEmployee] = useState('');
  const [expensePaymentMethod, setExpensePaymentMethod] = useState('cashbox');
  const [expenseDueDate, setExpenseDueDate] = useState('');
  // 🆕 Sub-category + ticket details + supplier dropdown for "tickets_payment"
  const [ticketsCategory, setTicketsCategory] = useState('amadeus');
  const [ticketDetails, setTicketDetails] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  // 🆕 Supplier transactions summary — shown live when the user picks a
  // supplier inside the supplier_payment / tickets_payment flow. Lets the
  // manager verify which sales each employee logged, whether they
  // uploaded a receipt, and whether the money has actually been
  // transferred to the central accounting.
  const [supplierTx, setSupplierTx] = useState(null);
  const [supplierTxLoading, setSupplierTxLoading] = useState(false);
  // 🆕 Mandatory document image for tickets_payment
  const [expenseImageFile, setExpenseImageFile] = useState(null);
  // 🆕 Quinzaine (15-day billing period) — Amadeus & most airlines settle
  // bi-monthly. Stored as "Q1-MM-YYYY" or "Q2-MM-YYYY".
  const [ticketPeriodQuinzaine, setTicketPeriodQuinzaine] = useState('Q1');
  const today = new Date();
  const [ticketPeriodMonth, setTicketPeriodMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'));
  const [ticketPeriodYear, setTicketPeriodYear] = useState(String(today.getFullYear()));
  // 🆕 Ledger (sigil "السجل") report state — same UX pattern as the
  // Financial Management ledger so users get a familiar experience.
  const [ledger, setLedger] = useState(null);
  const [ledgerStart, setLedgerStart] = useState(new Date().toISOString().split('T')[0]);
  const [ledgerEnd, setLedgerEnd] = useState(new Date().toISOString().split('T')[0]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState('all'); // all | income | expense

  // 🆕 Filters for the "قائمة المصاريف" panel — let the user narrow the
  // list (and any future export) by expense_type and supplier, mirroring
  // the supplier-side ledger so مصاريف الموردين match perfectly.
  const [listFilterType, setListFilterType] = useState('all');
  const [listFilterSupplier, setListFilterSupplier] = useState('all');

  // 🆕 Season filter — drives which rows feed the four KPI cards on top
  // (رصيد الخزينة / إجمالي المداخيل / إجمالي المصاريف / صافي الربح).
  // Default "current" = open season only (excludes data archived by
  // إغلاق الموسم). The dropdown is populated from /admin/season/available.
  const [seasonFilter, setSeasonFilter] = useState('current');
  const [availableSeasons, setAvailableSeasons] = useState([]);

  // Fetch overview data
  const fetchOverviewData = async (seasonOverride) => {
    if (!user || (user.role !== 'general_accountant' && user.role !== 'super_admin')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      const s = seasonOverride !== undefined ? seasonOverride : seasonFilter;
      if (s && s !== 'current') params.append('season', s);
      const response = await axios.get(`${API}/general-accounting?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSummary(response.data.summary);
      setRecentIncome(response.data.recent_income);
      setRecentExpenses(response.data.recent_expenses);
    } catch (error) {
      console.error('Error fetching general accounting data:', error);
      alert('فشل في تحميل بيانات المحاسبة العامة');
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Fetch cashbox chart data
  const fetchCashboxChart = useCallback(async (period, days) => {
    if (!user || (user.role !== 'super_admin' && user.role !== 'general_accountant')) return;
    setChartLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API}/general-accounting/cashbox-chart?period=${period}&days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setChartData(r.data);
    } catch (e) {
      console.error('Failed to load cashbox chart', e);
    } finally { setChartLoading(false); }
  }, [user]);

  // Fetch income list
  const fetchIncomeList = async () => {
    setIncomeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/general-income`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIncomeList(response.data);
    } catch (error) {
      console.error('Error fetching income:', error);
      alert('فشل في تحميل قائمة المداخيل');
    } finally {
      setIncomeLoading(false);
    }
  };

  // Fetch expenses list
  const fetchExpensesList = async () => {
    setExpensesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (listFilterType && listFilterType !== 'all') params.append('expense_type', listFilterType);
      if (listFilterSupplier && listFilterSupplier !== 'all') params.append('supplier_id', listFilterSupplier);
      const url = `${API}/general-expenses${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setExpensesList(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      alert('فشل في تحميل قائمة المصاريف');
    } finally {
      setExpensesLoading(false);
    }
  };

  // 🆕 Date-range ledger for general management — same UX as the
  // Financial Management ledger so users feel at home.
  const fetchLedger = async () => {
    if (!ledgerStart || !ledgerEnd) {
      alert('يرجى تحديد تاريخ البداية والنهاية');
      return;
    }
    setLedgerLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API}/general-accounting/ledger?start_date=${ledgerStart}&end_date=${ledgerEnd}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLedger(r.data);
    } catch (err) {
      console.error('Ledger fetch error:', err);
      alert('فشل في تحميل السجل: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLedgerLoading(false);
    }
  };

  // 🆕 Print the ledger as a branded PDF/Excel via the shared dialog.
  const handlePrintLedger = async () => {
    if (!ledger) {
      alert('يرجى إنشاء السجل أولاً ثم اضغط الطباعة');
      return;
    }
    try {
      await askPdfOrXlsx(
        `${API}/general-accounting/ledger/print`,
        {
          start_date: ledgerStart,
          end_date: ledgerEnd,
          type_filter: ledgerFilter,
        }
      );
    } catch (err) {
      console.error('Print error:', err);
      alert('فشل في الطباعة: ' + err.message);
    }
  };

  // Create manual income
  const handleCreateIncome = async () => {
    if (!incomeAmount || !incomeDescription) {
      alert('يرجى إدخال المبلغ والوصف');
      return;
    }

    if (parseFloat(incomeAmount) <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/general-income`, {
        income_type: incomeType,
        description: incomeDescription,
        amount: parseFloat(incomeAmount),
        source: incomeSource || null,
        invoice_number: incomeInvoice || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تسجيل المدخول بنجاح');
      setIncomeAmount('');
      setIncomeDescription('');
      setIncomeSource('');
      setIncomeInvoice('');
      fetchOverviewData();
      fetchIncomeList();
    } catch (error) {
      console.error('Error creating income:', error);
      alert('❌ فشل في تسجيل المدخول: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Create expense
  const handleCreateExpense = async () => {
    if (!expenseAmount || !expenseDescription) {
      alert('يرجى إدخال المبلغ والوصف');
      return;
    }

    if (parseFloat(expenseAmount) <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }

    // 🆕 Mandatory image for tickets_payment / supplier_payment — block at
    // the UI before hitting the API so the user gets a friendly Arabic message.
    if ((expenseType === 'tickets_payment' || expenseType === 'supplier_payment') && !expenseImageFile) {
      alert('📷 يرجى إرفاق صورة الوثيقة — مطلوبة للتسديد.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const createResp = await axios.post(`${API}/general-expenses`, {
        expense_type: expenseType,
        description: expenseDescription,
        amount: parseFloat(expenseAmount),
        vendor: expenseVendor || null,
        invoice_number: expenseInvoice || null,
        payment_method: expensePaymentMethod,
        employee_name: expenseEmployee || null,
        due_date: expenseDueDate || null,
        // Only send the tickets fields when expense_type matches.
        tickets_category: expenseType === 'tickets_payment' ? ticketsCategory : null,
        ticket_details: expenseType === 'tickets_payment' ? (ticketDetails || null) : null,
        ticket_period: expenseType === 'tickets_payment'
          ? `${ticketPeriodQuinzaine}-${ticketPeriodMonth}-${ticketPeriodYear}`
          : null,
        supplier_id: (expenseType === 'tickets_payment' || expenseType === 'supplier_payment')
          ? (supplierId || null) : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 🆕 If a file was attached, upload it right after creation. For
      // tickets_payment this is mandatory (validated above); for other
      // types it's optional but still supported.
      const newExpenseId = createResp.data?.expense_id;
      if (expenseImageFile && newExpenseId) {
        try {
          const fd = new FormData();
          fd.append('image', expenseImageFile);
          await axios.post(
            `${API}/general-expenses/${newExpenseId}/upload-image`,
            fd,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (imgErr) {
          // If image upload failed for a mandatory case, surface the
          // problem clearly — the expense exists but the manager won't
          // be able to approve until a valid image is attached.
          alert(
            '⚠️ تم إنشاء المصروف لكن فشل رفع الصورة: ' +
            (imgErr.response?.data?.detail || imgErr.message) +
            ((expenseType === 'tickets_payment' || expenseType === 'supplier_payment')
              ? '\nلن يستطيع المدير اعتماده حتى ترفع الصورة.' : '')
          );
        }
      }

      alert('✅ تم تسجيل المصروف بنجاح');
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseVendor('');
      setExpenseInvoice('');
      setExpenseEmployee('');
      setExpenseDueDate('');
      setTicketDetails('');
      setSupplierId('');
      setSupplierTx(null);
      setExpenseImageFile(null);
      // Reset the file input visually
      const fileInput = document.getElementById('expense-image-input');
      if (fileInput) fileInput.value = '';
      fetchOverviewData();
      fetchExpensesList();
    } catch (error) {
      console.error('Error creating expense:', error);
      alert('❌ فشل في تسجيل المصروف: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Approve expense
  const approveExpense = async (expenseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/general-expenses/${expenseId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم الموافقة على المصروف وخصمه من الخزينة');
      fetchOverviewData();
      fetchExpensesList();
    } catch (error) {
      console.error('Error approving expense:', error);
      alert('❌ فشل في الموافقة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Reject expense
  const rejectExpense = async (expenseId) => {
    if (!confirm('هل أنت متأكد من رفض هذا المصروف؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/general-expenses/${expenseId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم رفض المصروف');
      fetchOverviewData();
      fetchExpensesList();
    } catch (error) {
      console.error('Error rejecting expense:', error);
      alert('❌ فشل في الرفض: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 🆕 Reconcile flight cashbox operations with general ledger
  const handleReconcileFlightsCashbox = async () => {
    if (user?.role !== 'super_admin') {
      alert('هذه العملية متاحة للمدير العام فقط');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Dry-run first to preview
      const preview = await axios.post(
        `${API}/flights/admin/reconcile-cashbox?dry_run=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const r = preview.data;
      const total = r.airline_payments_synced + r.walkin_payments_synced + r.subagent_payments_synced;
      if (total === 0) {
        alert('✅ كل عمليات الرحلات مُسجَّلة بشكل صحيح. لا حاجة للمزامنة.');
        setLoading(false);
        return;
      }
      const msg = (
        `🔧 تم العثور على ${total} عملية غير مسجّلة في المحاسبة العامة:\n\n` +
        `• ${r.airline_payments_synced} دفعة طيران (مصروف)\n` +
        `• ${r.walkin_payments_synced} دفعة walk-in (دخل)\n` +
        `• ${r.subagent_payments_synced} دفعة وكالة شريكة (دخل)\n\n` +
        `📊 صافي الأثر على الخزينة: ${r.net_delta_on_cashbox.toLocaleString()} دج\n\n` +
        `هل تريد المتابعة بتسجيلها فعلياً؟`
      );
      if (!window.confirm(msg)) {
        setLoading(false);
        return;
      }
      const result = await axios.post(
        `${API}/flights/admin/reconcile-cashbox`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert(`✅ ${result.data.message}`);
      fetchOverviewData();
    } catch (error) {
      alert('❌ فشل في المزامنة: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  // NEW: Complete settlement function (Super Admin only)
  const handleCompleteSettlement = async () => {
    if (user?.role !== 'super_admin') {
      alert('هذه العملية متاحة للمدير العام فقط');
      return;
    }

    // Multiple confirmation steps for security
    const firstConfirm = confirm('⚠️ تحذير خطير: ستقوم بمخالصة كاملة لجميع حسابات الخزينة والمحاسبة العامة');
    if (!firstConfirm) return;

    const secondConfirm = confirm('🚨 هذا الإجراء سيحذف جميع المداخيل، المصاريف، عمليات الخزينة، والاسترجاع ويعيد كل شيء للصفر. هل أنت متأكد؟');
    if (!secondConfirm) return;

    const finalConfirm = confirm('⛔ إجراء غير قابل للاسترداد! سيتم تصفير:\n- رصيد الخزينة\n- جميع المداخيل\n- جميع المصاريف\n- جميع عمليات الاسترجاع\n\nهل أنت متأكد 100%؟');
    if (!finalConfirm) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/general-accounting/complete-settlement`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم إجراء المخالصة الكاملة بنجاح - جميع الحسابات في الصفر');
      
      // Refresh all data
      fetchOverviewData();
      fetchIncomeList();
      fetchExpensesList();
      
      console.log('Settlement completed:', response.data);
    } catch (error) {
      console.error('Error performing settlement:', error);
      alert('❌ فشل في إجراء المخالصة: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCashboxChart(chartPeriod, chartDays);
  }, [chartPeriod, chartDays, fetchCashboxChart]);

  useEffect(() => {
    fetchOverviewData();
    fetchCashboxChart(chartPeriod, chartDays);
    // 🆕 Pre-load suppliers list for the "tickets payment" sub-dropdown.
    // /api/suppliers is open to manager/accountant roles already.
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/suppliers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuppliers(Array.isArray(r.data) ? r.data : []);
      } catch {
        setSuppliers([]);
      }
    })();
    // 🆕 Pre-load the list of archived seasons for the filter dropdown
    // at the top of the Overview tab.
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/admin/season/available`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const seasons = (r.data?.items || [])
          .map(it => it.label || it.season_label)
          .filter(Boolean);
        setAvailableSeasons(seasons);
      } catch {
        setAvailableSeasons([]);
      }
    })();
  }, []);

  // 🆕 Auto-load the supplier's transactions whenever the user picks one
  // inside the supplier_payment / tickets_payment flow.
  useEffect(() => {
    const eligible =
      expenseType === 'supplier_payment' || expenseType === 'tickets_payment';
    if (!eligible || !supplierId) {
      setSupplierTx(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setSupplierTxLoading(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(
          `${API}/suppliers/${supplierId}/transactions-summary`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setSupplierTx(r.data);
      } catch {
        if (!cancelled) setSupplierTx(null);
      } finally {
        if (!cancelled) setSupplierTxLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [supplierId, expenseType]);

  useEffect(() => {
    if (activeTab === 'income') {
      fetchIncomeList();
    } else if (activeTab === 'expenses') {
      fetchExpensesList();
    } else if (activeTab === 'ledger' && !ledger) {
      // 🆕 First time entering the ledger tab — auto-fetch for the
      // default (today→today) so the user sees something immediately.
      fetchLedger();
    }
  }, [activeTab]);

  // 🆕 Re-fetch the expenses list whenever the filters change (only when
  // already on the Expenses tab — avoids an unnecessary call on mount).
  useEffect(() => {
    if (activeTab === 'expenses') {
      fetchExpensesList();
    }
  }, [listFilterType, listFilterSupplier]);

  // Only allow General Accountant and Super Admin
  if (!user || (user.role !== 'general_accountant' && user.role !== 'super_admin')) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-red-600">⚠️ ليس لديك صلاحية للوصول إلى محاسبة الإدارة العامة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📋 محاسبة الإدارة العامة</h1>
          <p className="text-sm text-gray-600 mt-1">إدارة مداخيل ومصاريف الإدارة العامة</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: '📊 نظرة عامة', icon: '📊' },
            { id: 'income', label: '📈 المداخيل', icon: '📈' },
            { id: 'expenses', label: '📉 المصاريف', icon: '📉' },
            { id: 'ledger', label: '📒 السجل / التقارير', icon: '📒' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`ga-tab-${tab.id}`}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && summary && (
        <div className="space-y-6">
          {/* 🆕 Season filter — lets the manager toggle between the open
              season, any archived season, or the lifetime view. */}
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border border-indigo-200 bg-indigo-50">
            <span className="font-bold text-indigo-900">📅 عرض الإحصائيات للموسم:</span>
            <select
              value={seasonFilter}
              onChange={(e) => {
                setSeasonFilter(e.target.value);
                fetchOverviewData(e.target.value);
              }}
              className="px-3 py-1.5 rounded-md border bg-white text-sm font-semibold text-indigo-900"
              data-testid="general-accounting-season-filter"
            >
              <option value="current">🟢 الموسم الحالي (المفتوح)</option>
              {availableSeasons.map((s) => (
                <option key={s} value={s}>📁 {s}</option>
              ))}
              <option value="all">📚 كل المواسم (مجموع تراكمي)</option>
            </select>
            {seasonFilter !== 'current' && (
              <button
                onClick={() => { setSeasonFilter('current'); fetchOverviewData('current'); }}
                className="px-2 py-1 rounded bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700"
                data-testid="general-accounting-reset-season"
              >
                ↺ عودة للموسم الحالي
              </button>
            )}
            <span className="text-xs text-indigo-700 italic">
              {seasonFilter === 'current' && 'الأرقام أدناه للموسم النشط فقط — البيانات المؤرشَفة لا تظهر هنا.'}
              {seasonFilter !== 'current' && seasonFilter !== 'all' && `أنت تعرض موسماً مغلقاً للقراءة فقط.`}
              {seasonFilter === 'all' && 'تجميع لكل المواسم — للمقارنة التاريخية.'}
            </span>
          </div>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-r from-purple-50 to-brand-50 border-purple-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-purple-600">رصيد الخزينة</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-700">
                  {summary.cashbox_balance?.toLocaleString()} دج
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-green-600">إجمالي المداخيل</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-700">
                  {summary.total_income?.toLocaleString()} دج
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-red-600">إجمالي المصاريف</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-700">
                  {summary.total_expenses?.toLocaleString()} دج
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-r from-brand-50 to-cyan-50 border-brand-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-brand-600">صافي الربح</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${summary.net_profit >= 0 ? 'text-brand-700' : 'text-red-700'}`}>
                  {summary.net_profit?.toLocaleString()} دج
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 🆕 Cashbox Evolution Chart */}
          <Card className="border-purple-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                    📈 تطور رصيد الخزينة
                  </CardTitle>
                  <CardDescription>
                    رؤية بصرية للسيولة عبر الزمن — يساعد على اكتشاف أي تذبذب غير طبيعي مبكراً
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={chartPeriod}
                    onChange={(e) => setChartPeriod(e.target.value)}
                    className="text-xs px-2 py-1 border rounded-md bg-white"
                    data-testid="chart-period-select"
                  >
                    <option value="daily">يومي</option>
                    <option value="weekly">أسبوعي</option>
                    <option value="monthly">شهري</option>
                  </select>
                  <select
                    value={chartDays}
                    onChange={(e) => setChartDays(parseInt(e.target.value, 10))}
                    className="text-xs px-2 py-1 border rounded-md bg-white"
                    data-testid="chart-days-select"
                  >
                    <option value={7}>آخر 7 أيام</option>
                    <option value={30}>آخر 30 يوم</option>
                    <option value={90}>آخر 3 أشهر</option>
                    <option value={180}>آخر 6 أشهر</option>
                    <option value={365}>آخر سنة</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {chartLoading ? (
                <div className="h-64 flex items-center justify-center text-gray-500 text-sm">⏳ جاري التحميل…</div>
              ) : !chartData || !chartData.points || chartData.points.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                  لا توجد بيانات في هذه الفترة لعرض الرسم البياني.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
                    <div className="bg-purple-50 border border-purple-200 rounded p-2">
                      <div className="text-purple-600">الرصيد الافتتاحي</div>
                      <div className="font-bold text-purple-800">{Math.round(chartData.opening_balance).toLocaleString()} دج</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded p-2">
                      <div className="text-green-600">إجمالي الدخل</div>
                      <div className="font-bold text-green-800">+{Math.round(chartData.total_income).toLocaleString()} دج</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <div className="text-red-600">إجمالي المصاريف</div>
                      <div className="font-bold text-red-800">-{Math.round(chartData.total_expense).toLocaleString()} دج</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <div className="text-blue-600">الرصيد الختامي</div>
                      <div className="font-bold text-blue-800">{Math.round(chartData.closing_balance).toLocaleString()} دج</div>
                    </div>
                  </div>
                  <div className="h-72 w-full" data-testid="cashbox-evolution-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData.points} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v/1000)}k`} />
                        <Tooltip
                          formatter={(v, name) => [`${Math.round(v).toLocaleString()} دج`, name]}
                          labelStyle={{ direction: 'rtl' }}
                          contentStyle={{ direction: 'rtl', fontSize: 12 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="income" name="دخل" fill="#10b981" />
                        <Bar dataKey="expense" name="مصروف" fill="#ef4444" />
                        <Line
                          type="monotone"
                          dataKey="balance"
                          name="رصيد الخزينة"
                          stroke="#7c3aed"
                          strokeWidth={3}
                          dot={{ fill: '#7c3aed', r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* 🆕 Reconcile flights cashbox button — fixes historical out-of-sync payments */}
          {user?.role === 'super_admin' && (
            <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300">
              <CardHeader>
                <CardTitle className="text-lg text-amber-800 flex items-center gap-2">
                  🔧 مزامنة عمليات الرحلات مع المحاسبة العامة
                </CardTitle>
                <CardDescription className="text-amber-700">
                  إذا لاحظت أن دفعات الطيران / walk-in / الوكالات الشريكة لا تظهر في رصيد الخزينة أعلاه،
                  اضغط هذا الزر لإعادة تسجيلها. سيُعرض لك أولاً ملخّص ما سيُسوَّى قبل التطبيق.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleReconcileFlightsCashbox}
                  className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-6 py-3"
                  disabled={loading}
                  data-testid="reconcile-flights-cashbox-btn"
                >
                  {loading ? 'جاري الفحص...' : '🔧 فحص + مزامنة عمليات الرحلات'}
                </Button>
                <p className="text-xs text-amber-700 mt-2">
                  💡 آمن للتشغيل — السجلات المُسوّاة سابقاً تُتجاهَل تلقائياً.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Super Admin Settlement Button */}
          {user?.role === 'super_admin' && (
            <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
              <CardHeader>
                <CardTitle className="text-lg text-red-700 flex items-center">
                  ⚠️ منطقة المدير العام - مخالصة كاملة
                </CardTitle>
                <CardDescription className="text-red-600">
                  هذا الإجراء سيحذف جميع البيانات المالية ويعيد النظام للصفر - استخدم بحذر شديد
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleCompleteSettlement}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3"
                  disabled={loading}
                >
                  {loading ? 'جاري المعالجة...' : '⛔ مخالصة كاملة للنظام'}
                </Button>
                <p className="text-xs text-red-500 mt-2">
                  ⚠️ تحذير: هذا الإجراء غير قابل للاسترداد ويتطلب تأكيدات متعددة
                </p>
              </CardContent>
            </Card>
          )}

          {/* Recent Activities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Income */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">📈 المداخيل الأخيرة</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {recentIncome.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">لا توجد مداخيل</p>
                  ) : (
                    recentIncome.map((income) => (
                      <div key={income.id} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-green-800">{income.description}</span>
                          <span className="font-bold text-green-700">+{income.amount?.toLocaleString()} دج</span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-green-600">
                          <span>{income.source}</span>
                          <span>{new Date(income.created_at).toLocaleDateString('ar-DZ')}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Expenses */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">📉 المصاريف الأخيرة</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {recentExpenses.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">لا توجد مصاريف</p>
                  ) : (
                    recentExpenses.map((expense) => (
                      <div key={expense.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-red-800">{expense.description}</span>
                          <span className="font-bold text-red-700">-{expense.amount?.toLocaleString()} دج</span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-red-600">
                          <span className={`px-2 py-1 rounded ${
                            expense.status === 'approved' ? 'bg-green-100 text-green-700' :
                            expense.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {expense.status === 'approved' ? 'مؤكد' :
                             expense.status === 'pending' ? 'في الانتظار' : 'مرفوض'}
                          </span>
                          <span>{new Date(expense.created_at).toLocaleDateString('ar-DZ')}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Income Tab */}
      {activeTab === 'income' && (
        <div className="space-y-6">
          {/* Add Income Form */}
          <Card>
            <CardHeader>
              <CardTitle>📈 إضافة مدخول جديد</CardTitle>
              <CardDescription>
                تسجيل مداخيل جانبية للإدارة العامة
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>نوع المدخول</Label>
                  <select
                    value={incomeType}
                    onChange={(e) => setIncomeType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="partnership">🤝 شراكة</option>
                    <option value="relations">👥 علاقات</option>
                    <option value="other_income">📋 مدخول آخر</option>
                  </select>
                </div>
                <div>
                  <Label>المبلغ (دج)</Label>
                  <Input
                    type="number"
                    value={incomeAmount}
                    onChange={(e) => setIncomeAmount(e.target.value)}
                    placeholder="مبلغ المدخول"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <Label>الوصف</Label>
                  <Input
                    value={incomeDescription}
                    onChange={(e) => setIncomeDescription(e.target.value)}
                    placeholder="وصف المدخول"
                  />
                </div>
                <div>
                  <Label>المصدر</Label>
                  <Input
                    value={incomeSource}
                    onChange={(e) => setIncomeSource(e.target.value)}
                    placeholder="اسم الشريك أو المصدر"
                  />
                </div>
                <div>
                  <Label>رقم الفاتورة (اختياري)</Label>
                  <Input
                    value={incomeInvoice}
                    onChange={(e) => setIncomeInvoice(e.target.value)}
                    placeholder="رقم الفاتورة"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleCreateIncome}
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={!incomeAmount || !incomeDescription}
                  >
                    📈 إضافة مدخول
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Income List */}
          <Card>
            <CardHeader>
              <CardTitle>📋 قائمة المداخيل</CardTitle>
            </CardHeader>
            <CardContent>
              {incomeLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {incomeList.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">لا توجد مداخيل مسجلة</p>
                  ) : (
                    incomeList.map((income) => (
                      <div key={income.id} className="p-4 border border-green-200 bg-green-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex flex-col">
                            <span className="font-medium text-green-800">{income.description}</span>
                            <span className="text-sm text-green-600">{income.source}</span>
                            {income.invoice_number && (
                              <span className="text-xs text-gray-600">فاتورة رقم: {income.invoice_number}</span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-green-700 text-lg">+{income.amount?.toLocaleString()} دج</span>
                            <div className="text-xs text-green-600">
                              {income.income_type === 'agency_transfer' && '📥 تحويل من وكالة'}
                              {income.income_type === 'manager_deposit' && '💰 ضخ من المدير'}
                              {income.income_type === 'partnership' && '🤝 شراكة'}
                              {income.income_type === 'relations' && '👥 علاقات'}
                              {income.income_type === 'other_income' && '📋 مدخول آخر'}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-sm text-green-700">
                          <span>{income.created_by_name}</span>
                          <span>{new Date(income.created_at).toLocaleDateString('ar-DZ', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="space-y-6">
          {/* Add Expense Form */}
          <Card>
            <CardHeader>
              <CardTitle>📉 إضافة مصروف جديد</CardTitle>
              <CardDescription>
                تسجيل مصاريف الإدارة العامة
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>نوع المصروف</Label>
                  <select
                    value={expenseType}
                    onChange={(e) => {
                      setExpenseType(e.target.value);
                      // Reset tickets sub-state when switching away from
                      // any supplier-linked type.
                      if (e.target.value !== 'tickets_payment' && e.target.value !== 'supplier_payment') {
                        setTicketDetails('');
                        setSupplierId('');
                        setSupplierTx(null);
                      }
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    data-testid="expense-type-select"
                  >
                    <option value="transport_bill">🚛 فاتورة نقل</option>
                    <option value="employee_salary">💰 راتب موظف</option>
                    <option value="office_rent">🏢 إيجار مكتب</option>
                    <option value="utilities">⚡ فواتير (كهرباء، ماء، إنترنت)</option>
                    <option value="maintenance">🔧 صيانة</option>
                    <option value="supplies">📚 مستلزمات مكتبية</option>
                    <option value="tickets_payment">✈️ تسديد تذاكر</option>
                    <option value="supplier_payment">📦 تسديد مورد</option>
                    <option value="other_expense">📋 مصروف آخر</option>
                  </select>
                </div>
                <div>
                  <Label>المبلغ (دج)</Label>
                  <Input
                    type="number"
                    value={expenseAmount}
                    onChange={(e) => setExpenseAmount(e.target.value)}
                    placeholder="مبلغ المصروف"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <Label>الوصف</Label>
                  <Input
                    value={expenseDescription}
                    onChange={(e) => setExpenseDescription(e.target.value)}
                    placeholder="وصف المصروف"
                  />
                </div>
                <div>
                  <Label>المورد/الجهة</Label>
                  <Input
                    value={expenseVendor}
                    onChange={(e) => setExpenseVendor(e.target.value)}
                    placeholder="اسم المورد أو الجهة"
                  />
                </div>
                <div>
                  <Label>طريقة الدفع</Label>
                  <select
                    value={expensePaymentMethod}
                    onChange={(e) => setExpensePaymentMethod(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="cashbox">🏦 من الخزينة</option>
                    <option value="bank_transfer">🏪 تحويل بنكي</option>
                    <option value="cash">💵 نقداً</option>
                  </select>
                </div>
                {expenseType === 'employee_salary' && (
                  <div>
                    <Label>اسم الموظف</Label>
                    <Input
                      value={expenseEmployee}
                      onChange={(e) => setExpenseEmployee(e.target.value)}
                      placeholder="اسم الموظف"
                    />
                  </div>
                )}

                {/* 🆕 Supplier-payment sub-form — generic supplier picker
                    + LIVE transactions summary for that supplier so the
                    manager can verify what each employee has logged and
                    whether the money was actually transferred. */}
                {expenseType === 'supplier_payment' && (
                  <>
                    <div className="md:col-span-2 lg:col-span-3">
                      <Label>🏢 المورد</Label>
                      <select
                        value={supplierId}
                        onChange={(e) => {
                          setSupplierId(e.target.value);
                          const found = suppliers.find(s => s.id === e.target.value);
                          if (found) setExpenseVendor(found.name);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        data-testid="supplier-payment-supplier-select"
                      >
                        <option value="">— اختر المورد —</option>
                        {suppliers.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* LIVE supplier transactions panel */}
                    {supplierId && (
                      <div className="md:col-span-2 lg:col-span-3 border-2 border-amber-300 rounded-lg p-4 bg-amber-50/30" data-testid="supplier-tx-panel">
                        {supplierTxLoading ? (
                          <div className="text-center py-6 text-gray-500">
                            ⏳ جارٍ تحميل سجل المورد…
                          </div>
                        ) : supplierTx ? (
                          <>
                            {/* Balance cards */}
                            <div className="grid grid-cols-3 gap-3 mb-4">
                              <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                                <div className="text-xs text-red-700">مبيعات (نحن مدينون)</div>
                                <div className="text-lg font-bold text-red-800" data-testid="supplier-tx-dues">
                                  {Number(supplierTx.balance?.total_dues || 0).toLocaleString()} دج
                                </div>
                              </div>
                              <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
                                <div className="text-xs text-green-700">دفعات سابقة</div>
                                <div className="text-lg font-bold text-green-800" data-testid="supplier-tx-paid">
                                  {Number(supplierTx.balance?.total_paid || 0).toLocaleString()} دج
                                </div>
                              </div>
                              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-center">
                                <div className="text-xs text-amber-800">الرصيد المستحق</div>
                                <div className="text-lg font-bold text-amber-900" data-testid="supplier-tx-balance">
                                  {Number(supplierTx.balance?.balance_due || 0).toLocaleString()} دج
                                </div>
                              </div>
                            </div>

                            {/* Sales table */}
                            <div className="mb-3">
                              <h4 className="font-bold text-sm mb-2 text-gray-800">
                                📋 المبيعات المسجَّلة على هذا المورد ({supplierTx.sales?.length || 0})
                              </h4>
                              {supplierTx.sales && supplierTx.sales.length > 0 ? (
                                <div className="max-h-72 overflow-y-auto border rounded">
                                  <table className="w-full text-xs">
                                    <thead className="bg-gray-100 sticky top-0">
                                      <tr>
                                        <th className="p-2 text-right">التاريخ</th>
                                        <th className="p-2 text-right">رقم الوصل</th>
                                        <th className="p-2 text-right">الموظف</th>
                                        <th className="p-2 text-right">العميل</th>
                                        <th className="p-2 text-right">التكلفة</th>
                                        <th className="p-2 text-right">الوثيقة</th>
                                        <th className="p-2 text-right">التحويل</th>
                                      </tr>
                                    </thead>
                                    <tbody data-testid="supplier-tx-sales-tbody">
                                      {supplierTx.sales.map((s, idx) => (
                                        <tr key={s.ledger_id || idx} className="border-t hover:bg-amber-50/50">
                                          <td className="p-2">{s.created_at ? formatDateWithEnglishNumerals(s.created_at) : '—'}</td>
                                          <td className="p-2 font-mono font-semibold text-brand-700" data-testid={`supplier-sale-receipt-${s.ledger_id}`}>
                                            {s.receipt_no || s.op_no || '—'}
                                            {s.op_type === 'ticket' && s.op_no && s.op_no !== s.receipt_no && (
                                              <div className="text-[10px] text-gray-500 font-normal mt-0.5" title="رقم التذكرة من شركة الطيران">
                                                ✈️ {s.op_no}
                                              </div>
                                            )}
                                          </td>
                                          <td className="p-2 font-semibold">{s.employee_name || '—'}</td>
                                          <td className="p-2">{s.client_name || s.description || '—'}</td>
                                          <td className="p-2 font-bold text-red-700">{Number(s.cost_price || 0).toLocaleString()} دج</td>
                                          <td className="p-2 text-center">
                                            {s.has_receipt ? (
                                              <a href={s.receipt_url?.startsWith('http') ? s.receipt_url : `${API.replace('/api', '')}${s.receipt_url}`}
                                                 target="_blank" rel="noopener noreferrer"
                                                 className="text-green-700 underline" title="عرض الوثيقة">
                                                ✅ متوفرة
                                              </a>
                                            ) : (
                                              <span className="text-red-700 font-bold" title="لا توجد وثيقة مرفقة">
                                                🔴 بلا وثيقة
                                              </span>
                                            )}
                                          </td>
                                          <td className="p-2 text-center">
                                            {s.transferred ? (
                                              <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded font-semibold">
                                                🟢 محوَّل
                                              </span>
                                            ) : (
                                              <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded font-semibold" title="المبلغ لم يصل إلى الإدارة العامة بعد">
                                                🟠 لم يُحوَّل
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 py-4 text-center bg-white rounded border">
                                  لا توجد مبيعات مسجَّلة على هذا المورد بعد.
                                </div>
                              )}
                            </div>

                            {/* Past payments */}
                            {supplierTx.payments && supplierTx.payments.length > 0 && (
                              <div>
                                <h4 className="font-bold text-sm mb-2 text-gray-800">
                                  💰 دفعات سابقة ({supplierTx.payments.length})
                                </h4>
                                <div className="max-h-40 overflow-y-auto border rounded bg-white">
                                  <table className="w-full text-xs">
                                    <thead className="bg-green-50 sticky top-0">
                                      <tr>
                                        <th className="p-2 text-right">التاريخ</th>
                                        <th className="p-2 text-right">المبلغ</th>
                                        <th className="p-2 text-right">المصدر</th>
                                        <th className="p-2 text-right">الموظف</th>
                                      </tr>
                                    </thead>
                                    <tbody data-testid="supplier-tx-payments-tbody">
                                      {supplierTx.payments.map((p, idx) => (
                                        <tr key={p.ledger_id || idx} className="border-t">
                                          <td className="p-2">{p.created_at ? formatDateWithEnglishNumerals(p.created_at) : '—'}</td>
                                          <td className="p-2 font-bold text-green-800">{Number(p.amount || 0).toLocaleString()} دج</td>
                                          <td className="p-2">
                                            {p.source === 'general_expense'
                                              ? <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">🏛️ مصاريف عامة</span>
                                              : <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded">⚡ دفع مباشر</span>}
                                          </td>
                                          <td className="p-2">{p.employee_name || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                              💡 <b>تفسير الشارات:</b> <span className="text-green-700">✅ متوفر</span> = الموظف رفع صورة الوصل /
                              <span className="text-red-700"> 🔴 بلا وصل</span> = الموظف لم يرفع صورة (المبلغ قد لا يكون حُوِّل) /
                              <span className="text-green-700"> 🟢 محوَّل</span> = المبلغ وصل للإدارة العامة /
                              <span className="text-orange-700"> 🟠 لم يُحوَّل</span> = المبلغ لا يزال في الوكالة.
                            </p>
                          </>
                        ) : (
                          <div className="text-center py-6 text-gray-500">
                            لا توجد بيانات.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* 🆕 Tickets-payment sub-form: category + supplier dropdown
                    (with optional free-text override in the vendor field above)
                    + ticket details. Only renders when expense_type is set
                    to 'tickets_payment'. */}
                {expenseType === 'tickets_payment' && (
                  <>
                    <div>
                      <Label>🎫 نوع التذاكر</Label>
                      <select
                        value={ticketsCategory}
                        onChange={(e) => setTicketsCategory(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        data-testid="tickets-category-select"
                      >
                        <option value="amadeus">✈️ تذاكر الأماديوس</option>
                        <option value="partner_company">🏢 تذاكر شركات مناولة</option>
                        <option value="umrah">🕋 تذاكر العمرة</option>
                        <option value="maritime">🚢 تذاكر بحرية</option>
                        <option value="land">🚌 تذاكر برية / حافلات</option>
                        <option value="misc">🎫 تذاكر متنوّعة</option>
                      </select>
                    </div>
                    <div>
                      <Label>🏢 المورد (الشركة الناقلة)</Label>
                      <select
                        value={supplierId}
                        onChange={(e) => {
                          setSupplierId(e.target.value);
                          // Auto-fill the vendor text field with the chosen
                          // supplier's name (still editable).
                          const found = suppliers.find(s => s.id === e.target.value);
                          if (found) setExpenseVendor(found.name);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        data-testid="tickets-supplier-select"
                      >
                        <option value="">— اختر مورداً مسجَّلاً (اختياري) —</option>
                        {suppliers.map(s => (
                          <option key={s.id} value={s.id}>{s.name}{s.type ? ` (${s.type})` : ''}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        إذا لم يكن المورد مسجَّلاً، اكتبه يدوياً في خانة "المورد/الجهة" أعلاه.
                      </p>
                    </div>
                    <div className="md:col-span-2 lg:col-span-3">
                      <Label>📝 تفاصيل التذكرة (رقم الرحلة، التاريخ، اسم العميل…)</Label>
                      <Input
                        value={ticketDetails}
                        onChange={(e) => setTicketDetails(e.target.value)}
                        placeholder="مثال: رحلة AH1010 — 2026/06/15 — الحاج محمد أحمد"
                        data-testid="ticket-details-input"
                      />
                    </div>

                    {/* 🆕 Quinzaine — la période de facturation de 15 jours. */}
                    <div className="md:col-span-2 lg:col-span-3 border border-amber-200 rounded p-3 bg-amber-50/50">
                      <Label className="text-amber-900 font-semibold">📅 فترة التذاكر (Quinzaine)</Label>
                      <p className="text-xs text-gray-600 mb-2">
                        أغلب شركات الطيران (Amadeus وغيرها) تُصدر فواتير كل نصف شهر.
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">الفترة</Label>
                          <select
                            value={ticketPeriodQuinzaine}
                            onChange={(e) => setTicketPeriodQuinzaine(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            data-testid="ticket-period-quinzaine-select"
                          >
                            <option value="Q1">Q1 — الأولى (1 → 15)</option>
                            <option value="Q2">Q2 — الثانية (16 → نهاية الشهر)</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">الشهر</Label>
                          <select
                            value={ticketPeriodMonth}
                            onChange={(e) => setTicketPeriodMonth(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            data-testid="ticket-period-month-select"
                          >
                            {[
                              ['01', 'جانفي'], ['02', 'فيفري'], ['03', 'مارس'], ['04', 'أفريل'],
                              ['05', 'ماي'], ['06', 'جوان'], ['07', 'جويلية'], ['08', 'أوت'],
                              ['09', 'سبتمبر'], ['10', 'أكتوبر'], ['11', 'نوفمبر'], ['12', 'ديسمبر'],
                            ].map(([v, n]) => <option key={v} value={v}>{v} — {n}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">السنة</Label>
                          <Input
                            type="number"
                            min="2020"
                            max="2099"
                            value={ticketPeriodYear}
                            onChange={(e) => setTicketPeriodYear(e.target.value)}
                            data-testid="ticket-period-year-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-amber-800 mt-2">
                        ✅ سيُحفظ بصيغة: <b dir="ltr">{ticketPeriodQuinzaine}-{ticketPeriodMonth}-{ticketPeriodYear}</b>
                      </p>
                    </div>
                  </>
                )}

                {/* 🆕 Document image — MANDATORY for tickets_payment,
                    optional otherwise. Without it the manager cannot
                    approve a tickets-payment expense (backend enforces
                    the same rule for safety). */}
                <div className="md:col-span-2 lg:col-span-3">
                  <Label>
                    {(expenseType === 'tickets_payment' || expenseType === 'supplier_payment') ? (
                      <span className="text-red-700">
                        📷 صورة الوثيقة / الإيصال
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded mr-2">إلزامي</span>
                      </span>
                    ) : (
                      <span>📷 صورة الوثيقة (اختياري)</span>
                    )}
                  </Label>
                  <Input
                    id="expense-image-input"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={(e) => setExpenseImageFile(e.target.files?.[0] || null)}
                    data-testid="expense-image-input"
                    className={(expenseType === 'tickets_payment' || expenseType === 'supplier_payment') && !expenseImageFile ? 'border-red-400' : ''}
                  />
                  {expenseImageFile && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-green-700">
                      ✅ تم اختيار: <b>{expenseImageFile.name}</b>
                      <span className="text-gray-500">({(expenseImageFile.size / 1024).toFixed(0)} KB)</span>
                      <button
                        type="button"
                        onClick={() => {
                          setExpenseImageFile(null);
                          const fi = document.getElementById('expense-image-input');
                          if (fi) fi.value = '';
                        }}
                        className="text-red-600 hover:text-red-700 ml-2"
                      >
                        ✕ إزالة
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    JPG / PNG حتى 2MB.
                    {(expenseType === 'tickets_payment' || expenseType === 'supplier_payment') && ' المدير لن يستطيع اعتماد المصروف بدون صورة.'}
                  </p>
                </div>

                <div>
                  <Label>رقم الفاتورة (اختياري)</Label>
                  <Input
                    value={expenseInvoice}
                    onChange={(e) => setExpenseInvoice(e.target.value)}
                    placeholder="رقم الفاتورة"
                  />
                </div>
                <div>
                  <Label>تاريخ الاستحقاق (اختياري)</Label>
                  <Input
                    type="date"
                    value={expenseDueDate}
                    onChange={(e) => setExpenseDueDate(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleCreateExpense}
                    className="w-full bg-red-600 hover:bg-red-700"
                    disabled={
                      !expenseAmount ||
                      !expenseDescription ||
                      // 🆕 Block submit when tickets_payment / supplier_payment missing image or supplier.
                      ((expenseType === 'tickets_payment' || expenseType === 'supplier_payment') && !expenseImageFile) ||
                      (expenseType === 'supplier_payment' && !supplierId)
                    }
                    data-testid="create-expense-btn"
                  >
                    📉 إضافة مصروف
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expenses List */}
          <Card>
            <CardHeader>
              <CardTitle>📋 قائمة المصاريف</CardTitle>
            </CardHeader>
            <CardContent>
              {/* 🆕 Filter bar — narrow by expense type and/or supplier so
                  مصاريف الموردين تطابق سجل المورد تماماً وتطبع منفصلة. */}
              <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="expenses-filter-bar">
                <div>
                  <Label className="text-xs text-gray-700">🏷️ نوع المصروف</Label>
                  <select
                    className="w-full mt-1 px-3 py-2 text-sm border border-amber-300 rounded-md bg-white"
                    value={listFilterType}
                    onChange={(e) => setListFilterType(e.target.value)}
                    data-testid="expense-list-filter-type"
                  >
                    <option value="all">— كل الأنواع —</option>
                    <option value="supplier_payment">📦 تسديد مورد</option>
                    <option value="tickets_payment">✈️ تسديد تذاكر</option>
                    <option value="employee_salary">💰 راتب موظف</option>
                    <option value="office_rent">🏢 إيجار مكتب</option>
                    <option value="utilities">⚡ فواتير</option>
                    <option value="maintenance">🔧 صيانة</option>
                    <option value="supplies">📚 مستلزمات</option>
                    <option value="transport_bill">🚛 فاتورة نقل</option>
                    <option value="other_expense">📋 مصروف آخر</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-700">🏢 حسب المورد</Label>
                  <select
                    className="w-full mt-1 px-3 py-2 text-sm border border-amber-300 rounded-md bg-white"
                    value={listFilterSupplier}
                    onChange={(e) => setListFilterSupplier(e.target.value)}
                    data-testid="expense-list-filter-supplier"
                  >
                    <option value="all">— كل الموردين —</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setListFilterType('all'); setListFilterSupplier('all'); }}
                    data-testid="expense-list-filter-clear"
                  >
                    🧹 مسح الفلاتر
                  </Button>
                  <div className="text-xs text-gray-600 mr-auto">
                    {expensesList.length > 0 && (
                      <>
                        <span className="font-bold">{expensesList.length}</span> سجل
                        {' • '}
                        <span className="font-bold text-red-700">
                          {expensesList.reduce((s, e) => s + (e.amount || 0), 0).toLocaleString()} دج
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {expensesLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {expensesList.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">لا توجد مصاريف مسجلة</p>
                  ) : (
                    expensesList.map((expense) => (
                      <div key={expense.id} className="p-4 border border-red-200 bg-red-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex flex-col">
                            <span className="font-medium text-red-800">{expense.description}</span>
                            <div className="text-sm text-red-600 space-x-2">
                              <span>{expense.vendor}</span>
                              {expense.employee_name && <span>• {expense.employee_name}</span>}
                              {expense.invoice_number && <span>• فاتورة: {expense.invoice_number}</span>}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              {expense.expense_type === 'transport_bill' && '🚛 فاتورة نقل'}
                              {expense.expense_type === 'employee_salary' && '💰 راتب موظف'}
                              {expense.expense_type === 'office_rent' && '🏢 إيجار مكتب'}
                              {expense.expense_type === 'utilities' && '⚡ فواتير'}
                              {expense.expense_type === 'maintenance' && '🔧 صيانة'}
                              {expense.expense_type === 'supplies' && '📚 مستلزمات'}
                              {expense.expense_type === 'tickets_payment' && (
                                <>
                                  ✈️ تسديد تذاكر
                                  {expense.tickets_category && (
                                    <span className="text-xs text-gray-600 mr-1">
                                      {' — '}
                                      {{
                                        amadeus: 'الأماديوس',
                                        partner_company: 'شركات مناولة',
                                        umrah: 'العمرة',
                                        maritime: 'بحرية',
                                        land: 'برية',
                                        misc: 'متنوّعة',
                                      }[expense.tickets_category] || expense.tickets_category}
                                    </span>
                                  )}
                                  {expense.ticket_period && (
                                    <span
                                      dir="ltr"
                                      className="inline-block mr-2 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold"
                                      data-testid={`ticket-period-badge-${expense.id}`}
                                    >
                                      📅 {expense.ticket_period}
                                    </span>
                                  )}
                                </>
                              )}
                              {expense.expense_type === 'supplier_payment' && '📦 تسديد مورد'}
                              {expense.expense_type === 'other_expense' && '📋 مصروف آخر'}
                              <span className="mx-2">•</span>
                              {expense.payment_method === 'cashbox' && '🏦 من الخزينة'}
                              {expense.payment_method === 'bank_transfer' && '🏪 تحويل بنكي'}
                              {expense.payment_method === 'cash' && '💵 نقداً'}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-red-700 text-lg">-{expense.amount?.toLocaleString()} دج</span>
                            <div className={`text-xs px-2 py-1 rounded mt-1 ${
                              expense.status === 'approved' ? 'bg-green-100 text-green-700' :
                              expense.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {expense.status === 'approved' ? '✅ مدفوع' :
                               expense.status === 'pending' ? '⏳ في الانتظار' : '❌ مرفوض'}
                            </div>
                          </div>
                        </div>
                        
                        {expense.status === 'pending' && (() => {
                          // 🔒 Tickets-payment expenses can only be
                          // approved/rejected by the manager (super_admin).
                          // The general accountant sees a locked badge.
                          const isTickets = expense.expense_type === 'tickets_payment';
                          const isManager = user?.role === 'super_admin';
                          const missingImage = isTickets && !expense.document_image_url;
                          if (isTickets && !isManager) {
                            return (
                              <div className="mt-3 p-2 rounded border border-amber-300 bg-amber-50 text-xs text-amber-800 flex items-center gap-2" data-testid="tickets-locked-badge">
                                🔒 <span><b>تسديد تذاكر</b> — يتطلب موافقة المدير. لا يمكن للمحاسب العام اعتماده.</span>
                              </div>
                            );
                          }
                          return (
                            <div className="mt-3 space-y-2">
                              {missingImage && (
                                <div className="p-2 rounded border border-red-300 bg-red-50 text-xs text-red-800" data-testid="missing-image-warning">
                                  📷 لا يمكن الاعتماد بدون صورة الوثيقة. يجب على المُنشئ رفعها أولاً.
                                </div>
                              )}
                              <div className="flex space-x-2">
                                <Button
                                  size="sm"
                                  onClick={() => approveExpense(expense.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                  data-testid={`approve-expense-${expense.id}`}
                                  disabled={missingImage}
                                >
                                  ✅ موافقة ودفع
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => rejectExpense(expense.id)}
                                  className="text-red-600 hover:text-red-700 border-red-300 text-xs"
                                  data-testid={`reject-expense-${expense.id}`}
                                >
                                  ❌ رفض
                                </Button>
                              </div>
                            </div>
                          );
                        })()}

                        {/* 🆕 Preview attached document image when present */}
                        {expense.document_image_url && (
                          <div className="mt-3 flex items-center gap-2 text-xs">
                            <span className="text-gray-700">📎 الوثيقة المرفقة:</span>
                            <a
                              href={`${API.replace(/\/api$/, '')}${expense.document_image_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-700 hover:text-indigo-900 underline"
                              data-testid={`view-image-${expense.id}`}
                            >
                              عرض الصورة في تبويب جديد
                            </a>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center text-sm text-red-700 mt-2">
                          <span>{expense.created_by_name}</span>
                          <span>{new Date(expense.created_at).toLocaleDateString('ar-DZ', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* 🆕 Ledger / Reports tab — same UX pattern as the Financial
          Management ledger (bank-statement style). */}
      {activeTab === 'ledger' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-amber-900 text-lg">📒 السجل المحاسبي للإدارة العامة (فترة قابلة للتحديد)</CardTitle>
              <p className="text-sm text-gray-600">
                كل المداخيل والمصاريف في الفترة المختارة برقم العملية والمبلغ والموظف، مع الحوصلة الكاملة والطباعة.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters row */}
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label>من تاريخ</Label>
                  <Input type="date" value={ledgerStart} onChange={(e) => setLedgerStart(e.target.value)}
                    className="w-44" data-testid="gen-ledger-start" />
                </div>
                <div>
                  <Label>إلى تاريخ</Label>
                  <Input type="date" value={ledgerEnd} onChange={(e) => setLedgerEnd(e.target.value)}
                    className="w-44" data-testid="gen-ledger-end" />
                </div>
                <div>
                  <Label>نوع الحركة</Label>
                  <select value={ledgerFilter} onChange={(e) => setLedgerFilter(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md w-44" data-testid="gen-ledger-filter">
                    <option value="all">📋 الكل</option>
                    <option value="income">💚 المداخيل فقط</option>
                    <option value="expense">🔴 المصاريف فقط</option>
                  </select>
                </div>
                <Button onClick={fetchLedger}
                  disabled={ledgerLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="gen-ledger-generate-btn">
                  {ledgerLoading ? '⏳ جاري التحميل…' : '📊 إنشاء السجل'}
                </Button>
                {ledger && (
                  <Button onClick={handlePrintLedger}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="gen-ledger-print-btn">
                    🖨️ طباعة السجل
                  </Button>
                )}
              </div>

              {ledger && (
                <div className="space-y-4">
                  {/* Summary cards (6 numbers) */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-center" data-testid="gen-ledger-summary">
                    {[
                      { label: '💚 المداخيل', val: ledger.summary?.total_income, cls: 'text-green-700 bg-green-50 border-green-200' },
                      { label: '🔴 المصاريف', val: ledger.summary?.total_expenses, cls: 'text-red-700 bg-red-50 border-red-200' },
                      { label: '⏳ معلَّقة', val: ledger.summary?.pending_expenses, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
                      { label: '💰 الافتتاحي', val: ledger.summary?.opening_balance, cls: 'text-gray-700 bg-gray-50 border-gray-200' },
                      { label: '🏦 الختامي (الخزينة)', val: ledger.summary?.closing_balance, cls: 'text-purple-700 bg-purple-50 border-purple-200' },
                      { label: '📊 صافي الفترة', val: ledger.summary?.net_change,
                        cls: (ledger.summary?.net_change || 0) >= 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-rose-700 bg-rose-50 border-rose-200' },
                    ].map((c, i) => (
                      <div key={i} className={`border-2 rounded-lg p-2 ${c.cls}`}>
                        <div className="text-xs font-semibold">{c.label}</div>
                        <div className="text-base font-extrabold mt-1">
                          {Number(c.val || 0).toLocaleString()} دج
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* By-category breakdown (expenses) */}
                  {ledger.by_category?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="gen-ledger-by-category">
                      <div className="font-bold text-amber-900 mb-2">🧮 حوصلة المصاريف حسب الفئة:</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {ledger.by_category.map((c, i) => (
                          <div key={i} className="bg-white rounded p-2 border border-amber-200">
                            <div className="text-xs text-gray-600">{c.label}</div>
                            <div className="text-sm font-bold text-amber-800">
                              {Number(c.total || 0).toLocaleString()} دج
                              <span className="text-xs text-gray-500 mr-1">({c.count})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lines table */}
                  <div className="overflow-x-auto">
                    <div className="text-sm text-gray-600 mb-1">
                      عدد العمليات: <b>{ledger.lines?.length || 0}</b>
                      {ledgerFilter !== 'all' && <span className="text-amber-700"> · فلتر مفعَّل</span>}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-right">النوع</TableHead>
                          <TableHead className="text-right">رقم العملية</TableHead>
                          <TableHead className="text-right">التاريخ والوقت</TableHead>
                          <TableHead className="text-right">الفئة</TableHead>
                          <TableHead className="text-right">الوصف</TableHead>
                          <TableHead className="text-right">الموظف</TableHead>
                          <TableHead className="text-right">الحالة</TableHead>
                          <TableHead className="text-right">المبلغ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(ledger.lines || []).map((l, i) => (
                          <TableRow key={i} data-testid={`gen-ledger-line-${i}`}>
                            <TableCell className="text-sm">{l.type_label}</TableCell>
                            <TableCell className="text-sm font-mono" dir="ltr">{l.ref}</TableCell>
                            <TableCell className="text-xs text-gray-700" dir="ltr">{l.datetime}</TableCell>
                            <TableCell className="text-sm">{l.category_label || l.category}</TableCell>
                            <TableCell className="text-sm">{l.description}</TableCell>
                            <TableCell className="text-sm">{l.employee}</TableCell>
                            <TableCell className="text-xs">
                              {l.status === 'approved' && <span className="text-green-700 font-semibold">✅ معتمد</span>}
                              {l.status === 'pending' && <span className="text-amber-700 font-semibold">⏳ معلَّق</span>}
                              {l.status === 'rejected' && <span className="text-red-700 font-semibold">❌ مرفوض</span>}
                            </TableCell>
                            <TableCell className={`text-sm font-bold ${
                              l.sign === '+' ? 'text-green-700' : (l.sign === '-' ? 'text-red-700' : 'text-gray-500')
                            }`}>
                              {l.sign} {Number(l.amount || 0).toLocaleString()} دج
                            </TableCell>
                          </TableRow>
                        ))}
                        {(ledger.lines?.length || 0) === 0 && (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center text-gray-500 py-6">
                              لا توجد عمليات في هذه الفترة
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
});

export default GeneralAccountingManagement;
